import { getApiUrl } from '../utils/apiUrl';

const getApiBase = () => getApiUrl();

// ========== RESOURCES (machines + employees) ==========

export async function fetchProductionResources(token, teamId = null) {
  const url = teamId 
    ? `${getApiBase()}/production-plan/resources?teamId=${teamId}`
    : `${getApiBase()}/production-plan/resources`;
    
  const response = await fetch(url, {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  });
  
  if (!response.ok) {
    throw new Error('Failed to fetch resources');
  }
  
  return response.json();
}

export async function addOperatorsToProductionJob(id, operators, token) {
  const response = await fetch(`${getApiBase()}/production-plan/jobs/${id}/operators`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({ operators })
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(errorText || 'Failed to add operators');
  }

  return response.json();
}

// ========== ALL EMPLOYEES (read-only) ==========

export async function fetchAllProductionJobs(token) {
  const response = await fetch(`${getApiBase()}/production-plan/jobs/all`, {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  });
  
  if (!response.ok) {
    throw new Error('Failed to fetch all jobs');
  }
  
  return response.json();
}

export async function fetchAllEmployeeResources(token) {
  const response = await fetch(`${getApiBase()}/production-plan/resources/all-employees`, {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  });
  
  if (!response.ok) {
    throw new Error('Failed to fetch all employees');
  }
  
  return response.json();
}

// ========== RESOURCE WORKLOAD ==========

export async function fetchResourceWorkload(resourceId, token) {
  const response = await fetch(`${getApiBase()}/production-plan/resource-workload/${resourceId}`, {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  });
  
  if (!response.ok) {
    throw new Error('Failed to fetch resource workload');
  }
  
  return response.json();
}

// ========== MACHINES (uses existing machines table) ==========

export async function fetchProductionMachines(token, teamId = null) {
  const url = teamId 
    ? `${getApiBase()}/production-plan/machines?teamId=${teamId}`
    : `${getApiBase()}/production-plan/machines`;
    
  const response = await fetch(url, {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  });
  
  if (!response.ok) {
    throw new Error('Failed to fetch machines');
  }
  
  return response.json();
}

// ========== PLANNED HOURS (batch) ==========

export async function fetchBatchPlannedHours(token) {
  const response = await fetch(`${getApiBase()}/production-plan/planned-hours`, {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  });
  
  if (!response.ok) {
    throw new Error('Failed to fetch planned hours');
  }
  
  return response.json();
}

// ========== JOBS BY ORDER (for tooltips) ==========

export async function fetchJobsByOrder(token) {
  const response = await fetch(`${getApiBase()}/production-plan/jobs-by-order`, {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  });
  
  if (!response.ok) {
    throw new Error('Failed to fetch jobs by order');
  }
  
  return response.json();
}

// ========== MY JOBS (for employee calendar) ==========

export async function fetchMyJobs(token, startDate = null, endDate = null) {
  let url = `${getApiBase()}/production-plan/my-jobs`;
  
  const params = new URLSearchParams();
  if (startDate) params.append('startDate', startDate);
  if (endDate) params.append('endDate', endDate);
  
  if (params.toString()) {
    url += `?${params.toString()}`;
  }
  
  console.log('[fetchMyJobs] Fetching from:', url);
  
  const response = await fetch(url, {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  });
  
  console.log('[fetchMyJobs] Response status:', response.status);
  
  if (!response.ok) {
    const errorText = await response.text();
    console.error('[fetchMyJobs] Error response:', errorText);
    throw new Error(`Failed to fetch my jobs: ${response.status} ${errorText}`);
  }
  
  const data = await response.json();
  console.log('[fetchMyJobs] Data received:', data);
  return data;
}

// ========== JOBS ==========

export async function fetchProductionJobs(token) {
  const response = await fetch(`${getApiBase()}/production-plan/jobs`, {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  });
  
  if (!response.ok) {
    throw new Error('Failed to fetch production jobs');
  }
  
  return response.json();
}

export async function createProductionJob(data, token) {
  const response = await fetch(`${getApiBase()}/production-plan/jobs`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify(data)
  });
  
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.message || err.error || 'Failed to create production job');
  }
  
  return response.json();
}

export async function updateProductionJob(id, data, token) {
  const response = await fetch(`${getApiBase()}/production-plan/jobs/${id}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify(data)
  });
  
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.detail || err.message || 'Failed to update production job');
  }
  
  return response.json();
}

export async function completeProductionJob(id, token, date = null) {
  const response = await fetch(`${getApiBase()}/production-plan/jobs/${id}/complete`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({ date })
  });
  
  if (!response.ok) {
    throw new Error('Failed to complete production job');
  }
  
  return response.json();
}

export async function uncompleteProductionJob(id, token) {
  const response = await fetch(`${getApiBase()}/production-plan/jobs/${id}/uncomplete`, {
    method: 'PUT',
    headers: {
      'Authorization': `Bearer ${token}`
    }
  });
  
  if (!response.ok) {
    throw new Error('Failed to uncomplete production job');
  }
  
  return response.json();
}

export async function deleteProductionJob(id, token) {
  const response = await fetch(`${getApiBase()}/production-plan/jobs/${id}`, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${token}`
    }
  });
  
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.detail || err.message || 'Failed to delete production job');
  }
  
  return response.json();
}

export async function deleteOperatorFromJob(jobId, resourceId, token, start = null, end = null) {
  // Build URL with optional query parameters for start/end times
  let url = `${getApiBase()}/production-plan/jobs/${jobId}/operators/${resourceId}`;
  
  if (start && end) {
    const params = new URLSearchParams({ start, end });
    url += `?${params.toString()}`;
  }
  
  const response = await fetch(url, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${token}`
    }
  });
  
  if (!response.ok) {
    throw new Error('Failed to delete operator');
  }
  
  return response.json();
}

export async function revertProductionJob(auditLogId, token) {
  const response = await fetch(`${getApiBase()}/production-plan/jobs/revert`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({ auditLogId })
  });

  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.message || 'Kunne ikke tilbakestille endring');
  }

  return response.json();
}

// ========== EXCLUDED EMPLOYEES ==========

export async function fetchExcludedEmployees(token) {
  const response = await fetch(`${getApiBase()}/production-plan/exclusions`, {
    headers: { 'Authorization': `Bearer ${token}` }
  });
  if (!response.ok) throw new Error('Kunne ikke hente ekskluderte ansatte');
  return response.json();
}

export async function addExcludedEmployee(employeeId, token) {
  const response = await fetch(`${getApiBase()}/production-plan/exclusions`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
    body: JSON.stringify({ employeeId })
  });
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.message || 'Kunne ikke ekskludere ansatt');
  }
  return response.json();
}

export async function removeExcludedEmployee(employeeId, token) {
  const response = await fetch(`${getApiBase()}/production-plan/exclusions/${employeeId}`, {
    method: 'DELETE',
    headers: { 'Authorization': `Bearer ${token}` }
  });
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.message || 'Kunne ikke fjerne ekskludering');
  }
  return response.json();
}
